import { Link } from "react-router-dom";
import { Camera, TrendingUp, MessageSquare, Headphones, Sparkles, Wrench, Package, FileText, Megaphone, LayoutDashboard, Check, ArrowRight, PhoneCall, ClipboardCheck, Rocket, KeyRound, MapPin, Star, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const SERVICES = [
  { icon: Camera, title: "Professional Photography", desc: "Magazine-quality imagery that makes your listing impossible to scroll past." },
  { icon: TrendingUp, title: "Revenue & Dynamic Pricing", desc: "Data-driven nightly rates that squeeze every dollar out of peak demand." },
  { icon: MessageSquare, title: "Guest Communication", desc: "Warm, lightning-fast replies across every channel — so you never lift a finger." },
  { icon: Headphones, title: "24/7 Support", desc: "A real local team on call for your guests, day and night, 365 days a year." },
  { icon: Sparkles, title: "Housekeeping & Turnovers", desc: "Inventory, restocking, cleaning, minor repairs and claims — handled every single time." },
  { icon: Wrench, title: "Maintenance Coordination", desc: "Proactive upkeep with our trusted San Antonio & Hill Country vendors." },
  { icon: Package, title: "Supply Management", desc: "Restocked essentials so your guests never run short and reviews stay five-star." },
  { icon: FileText, title: "Owner Statements & Accounting", desc: "Transparent monthly statements and clean books you can actually understand." },
  { icon: Megaphone, title: "Listing Optimization", desc: "SEO-tuned copy and staging that turns lookers into confirmed bookings." },
  { icon: ShieldCheck, title: "Compliance Handled", desc: "We navigate local STR regulations and permits so you stay 100% above board." },
];

const CHANNELS = ["Airbnb", "VRBO", "Booking.com", "Google Vacation Rentals", "Your Own Direct Website"];

const STEPS = [
  { icon: PhoneCall, title: "Your Welcome Call", desc: "A free, no-obligation consultation on your home's true earning potential." },
  { icon: ClipboardCheck, title: "Property Assessment", desc: "We meet you at your property and deliver an accurate, data-backed income estimate." },
  { icon: Rocket, title: "Your Onboarding", desc: "We set your property up for success — professional photography and staging included." },
  { icon: Megaphone, title: "Listing Optimization", desc: "We create, optimize and manage a listing built to stand out in a crowded market." },
  { icon: KeyRound, title: "Our Management", desc: "Drive off into the sunset. We look after your home like our own and delight every guest." },
  { icon: Sparkles, title: "Turnover Service", desc: "Full-service turnovers: inventory, restocking, cleaning, minor repairs and claims." },
];

const AREAS = ["San Antonio", "New Braunfels", "Schertz / Cibolo", "Canyon Lake", "Texas Hill Country"];

export default function PropertyManagement() {
  return (
    <div>
      <div className="relative overflow-hidden bg-ink py-24 text-white">
        <img src="https://customer-assets-v7afamib.emergentagent.net/job_elite-booking-10/artifacts/a0ja5s4p_DJI_0236.webp" alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative mx-auto max-w-4xl px-5 text-center lg:px-8">
          <span className="overline text-gold">Property Management · San Antonio & the Texas Hill Country</span>
          <h1 className="mt-4 text-4xl leading-tight lg:text-6xl">We don't just manage rentals.<br />We build profitable vacation rental businesses.</h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-white/70">
            Elite STR Hosting Co. turns your property into a thriving, hands-off income stream — with white-glove hosting, dynamic pricing, and a local team that treats your home like our own.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/owner-services"><Button className="rounded-full bg-white px-6 text-ink hover:bg-camel hover:text-white">Free Revenue Projection</Button></Link>
            <a href="tel:+12102743556"><Button variant="outline" className="rounded-full border-white/30 bg-transparent px-6 text-white hover:bg-white hover:text-ink"><PhoneCall size={16} className="mr-2" /> (210) 274-3556</Button></a>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        {/* WHY US STATS */}
        <div className="grid grid-cols-2 gap-6 rounded-3xl bg-cream p-8 text-center lg:grid-cols-4">
          {[
            { v: "17+ yrs", l: "Local market expertise" },
            { v: "+32%", l: "Avg. revenue increase" },
            { v: "91%", l: "Average occupancy" },
            { v: "365", l: "Days a year on call" },
          ].map((s, i) => (
            <div key={i}><div className="text-3xl font-medium text-ink lg:text-4xl">{s.v}</div><div className="overline mt-1">{s.l}</div></div>
          ))}
        </div>

        {/* FOUNDERS */}
        <div className="mt-20 grid gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <span className="overline">Meet your hosts</span>
            <h2 className="mt-2 text-3xl lg:text-4xl">Kristy &amp; Gregg Russo</h2>
            <p className="mt-4 text-lg leading-relaxed text-ink/80">
              We're the husband-and-wife team behind Elite STR Hosting Co. <strong>Kristy</strong> is a local Realtor and entrepreneur who has done business in Northeast San Antonio for over 17 years — a New Braunfels homeowner and former San Antonio and Schertz/Cibolo resident who knows these neighborhoods, demographics, trends, and world-famous attractions inside and out. <strong>Gregg</strong> is a retired U.S. Army Staff Sergeant who brings discipline, precision, and relentless follow-through to every property we manage.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-ink/80">
              Together we're the dynamic duo that rocks the short-term rental market from San Antonio to the Texas Hill Country — and we'd love to do the same for your property.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {AREAS.map((a) => (
                <span key={a} className="flex items-center gap-1.5 rounded-full border border-black/10 px-3 py-1.5 text-sm text-ink">
                  <MapPin size={13} className="text-camel" /> {a}
                </span>
              ))}
            </div>
          </div>
          <div className="overflow-hidden rounded-3xl">
            <img src="https://customer-assets-v7afamib.emergentagent.net/job_elite-booking-10/artifacts/5krwy2t0_DSC05819.webp" alt="Elite STR managed property" className="h-full w-full object-cover" />
          </div>
        </div>

        {/* PROCESS */}
        <div className="mt-20">
          <div className="text-center">
            <span className="overline">How it works</span>
            <h2 className="mt-2 text-3xl lg:text-4xl">Our Airbnb management process</h2>
            <p className="mx-auto mt-3 max-w-xl text-taupe">Six simple steps from "curious owner" to a fully managed, profit-generating rental.</p>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {STEPS.map((s, i) => (
              <div key={i} className="rounded-3xl border border-black/5 bg-white p-6">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-ink text-white"><s.icon size={19} strokeWidth={1.5} /></span>
                  <span className="text-3xl font-serif text-camel">0{i + 1}</span>
                </div>
                <h3 className="mt-4 text-xl">{s.title}</h3>
                <p className="mt-2 text-sm text-taupe">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* SERVICES */}
        <div className="mt-20">
          <div className="mb-10 text-center">
            <span className="overline">Full-service, end to end</span>
            <h2 className="mt-2 text-3xl lg:text-4xl">Everything your property needs</h2>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <div key={i} className="rounded-3xl border border-black/5 bg-white p-6 transition-colors hover:border-camel">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-cream text-ink"><s.icon size={20} strokeWidth={1.5} /></span>
                <h3 className="mt-4 text-xl">{s.title}</h3>
                <p className="mt-2 text-sm text-taupe">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CHANNELS */}
        <div className="mt-16 grid gap-8 rounded-3xl bg-cream p-10 lg:grid-cols-2 lg:items-center">
          <div>
            <span className="overline">Maximum exposure</span>
            <h2 className="mt-2 text-3xl lg:text-4xl">Listed everywhere that matters</h2>
            <p className="mt-3 text-taupe">One property, every major channel — synced in real time to prevent double bookings and keep your calendar full.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {CHANNELS.map((c) => (
              <span key={c} className="flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-sm font-medium text-ink shadow-sm">
                <Check size={15} className="text-camel" /> {c}
              </span>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 rounded-3xl bg-ink p-10 text-center text-white md:p-14">
          <Star className="mx-auto mb-3 fill-gold text-gold" />
          <h2 className="text-3xl lg:text-4xl">Ready to earn more with less stress?</h2>
          <p className="mx-auto mt-3 max-w-lg text-white/70">Submit your property for a free, no-obligation projection of your profit potential — or just give us a call, 8am–8pm daily.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/owner-services"><Button className="rounded-full bg-white px-6 text-ink hover:bg-camel hover:text-white">Get my free projection <ArrowRight size={16} className="ml-1" /></Button></Link>
            <a href="tel:+12102743556"><Button variant="outline" className="rounded-full border-white/30 bg-transparent px-6 text-white hover:bg-white hover:text-ink">Call (210) 274-3556</Button></a>
          </div>
        </div>
      </div>
    </div>
  );
}
